import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { financeApi } from '@/api/finance.api';
import type { TrialBalanceLine, AccountingEvent } from '@/types/finance';
import { showApiError } from '@/utils/apiError';

export function FinanceLedgerPage() {
  const eventPagination = usePagination(15);
  const [rows, setRows] = useState<TrialBalanceLine[]>([]);
  const [events, setEvents] = useState<AccountingEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const r = await financeApi.listAccountingEvents({
        page: eventPagination.page,
        size: eventPagination.size,
      });
      setEvents(r.content);
      eventPagination.applyPageResponse(r);
    } catch {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [eventPagination.page, eventPagination.size, eventPagination]);

  useEffect(() => {
    financeApi
      .trialBalance()
      .then(setRows)
      .catch((err) => showApiError(err, 'Failed to load trial balance'));
  }, []);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  return (
    <div>
      <PageHeader
        title="General Ledger"
        description="Trial balance and accounting event drill-down — source document → event → journal → ledger"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Ledger' },
        ]}
      />
      <div className="card overflow-x-auto mb-4">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700">
              <th className="p-3">Code</th>
              <th className="p-3">Account</th>
              <th className="p-3">Type</th>
              <th className="p-3 text-right">Debit</th>
              <th className="p-3 text-right">Credit</th>
              <th className="p-3 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.accountId} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3 font-mono">{row.accountCode}</td>
                <td className="p-3">{row.accountName}</td>
                <td className="p-3">{row.accountType}</td>
                <td className="p-3 text-right">{row.debitTotal}</td>
                <td className="p-3 text-right">{row.creditTotal}</td>
                <td className="p-3 text-right font-medium">{row.balance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card overflow-x-auto">
        <h3 className="font-semibold p-3 border-b">Recent accounting events</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Event</th>
              <th className="p-2 text-left">Source</th>
              <th className="p-2 text-right">Amount</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Journal</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.id} className="border-b">
                <td className="p-2">{ev.eventDate}</td>
                <td className="p-2">{ev.eventType}</td>
                <td className="p-2">
                  {ev.sourceReferenceType}
                  {ev.sourceReferenceId != null ? ` #${ev.sourceReferenceId}` : ''}
                </td>
                <td className="p-2 text-right font-mono">{ev.totalAmount?.toFixed(2)}</td>
                <td className="p-2">{ev.statusCode}</td>
                <td className="p-2">{ev.journalEntryId != null ? `#${ev.journalEntryId}` : '—'}</td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr>
                <td colSpan={6} className="p-4 text-slate-500">
                  Events appear when sub-ledgers publish transactions (POS, procurement, payroll, etc.)
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <TablePagination
          page={eventPagination.page}
          totalPages={eventPagination.totalPages}
          totalElements={eventPagination.totalElements}
          onPageChange={eventPagination.setPage}
          loading={eventsLoading}
          hasRows={events.length > 0}
        />
      </div>
    </div>
  );
}
